import {
  ROUTE_PAYMENT_COLLECT_PAYMENT_METHOD,
  ROUTE_SIGN_IN,
  ROUTE_SIGN_UP,
} from '@/constants/routes';
import { getRoute, getRouteLoadingScreen } from '@/utils';
import axios, { isAxiosError } from 'axios';
import authService from '../services/auth.service';
import envService from '../services/env.service';
import sentryService from '../services/sentry.service';

/**
 * Singleton to make requests to the server
 */
const apiClient = axios.create({
  baseURL: envService.getEnv().VITE_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

/**
 * Add a request interceptor to add the token to the headers
 */
apiClient.interceptors.request.use(
  (config) => {
    // Send the authenticated user token if available
    const token = authService.getToken();
    if (token) {
      config.headers['x-authorization'] = `${token}`;
    }

    // Send the visitor session token if available
    const visitorSessionToken = authService.getVisitorToken();
    if (visitorSessionToken) {
      config.headers['x-session-authorization'] = `${visitorSessionToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error === undefined) {
      return Promise.reject('Unknown error');
    }
    if (!isAxiosError(error)) {
      return Promise.reject(error);
    }

    // Capture API errors in Sentry with context.
    // Exclude expected responses handled gracefully by the UI:
    // - 400 on sign-in = unregistered email, surfaced as a form validation message
    // - 405 "Risk not processed" = user risk is still being calculated, redirected to loading screen
    const isExpectedError =
      (error.response?.status === 400 && error.config?.url === '/auth/sign-in') ||
      (error.response?.status === 405 && error.response?.data?.title === 'Risk not processed');

    if (error.response && !isExpectedError) {
      sentryService.captureException(error, {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response.status,
        statusText: error.response.statusText,
        responseData: error.response.data,
      });
    }

    if (error?.response?.status === 401 && error?.response?.data?.message !== 'Session unauthorized') {
      authService.logout();
      window.location.href = getRoute(ROUTE_SIGN_IN);
      return Promise.reject(error);
    } else if (error?.response?.status === 401 && error?.response?.data?.message === 'Session unauthorized') {
      authService.logoutVisitor();
      return Promise.reject(error);
    } else if (error?.response?.status === 402) {
      authService.logout();
      window.location.href = getRoute(ROUTE_PAYMENT_COLLECT_PAYMENT_METHOD);
      return Promise.reject(error);
    } else if (error?.response?.status === 403 && error.response.data.title === 'Invite required') {
      authService.logout();
      window.location.href = getRoute(ROUTE_SIGN_UP);
      return Promise.reject(error);
    } else if (error?.response?.status === 405 && error?.response?.data.title === 'Risk not processed') {
      const path = window.location.pathname;
      if (path.split('/')[1] !== 'loading-screen') window.location.href = getRouteLoadingScreen(path);
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
